/* eslint-disable no-mixed-spaces-and-tabs */
import i from 'i18next'
import { useState, useRef, useEffect } from 'react'
import {
	motion,
	useScroll,
	useTransform,
	AnimateSharedLayout,
} from 'framer-motion'
import useMedia from 'use-media'
import { ProductDetail } from 'lib/hooks/use-products'
import { useWindowSize } from 'lib/hooks/use-window'
import Portal from 'components/Portal'
import { res } from 'components/Image'
import { fixPageNav } from './Detail'
import { headers as ease } from 'constants/easings'
import { mobileMenuMaxWidth } from 'constants/styles'
import Buy from './Buy'
import BuyButton from './BuyButton'

const sections = ({ packs, faqs, reviews }) => (
	[
		'benefits',
		'ingredients',
		packs && 'packs',
		'packaging',
		faqs && 'faq',
		reviews && 'reviews',
	].filter( Boolean )
)

type Props = {
	product: ProductDetail
	variant: ProductDetail[ 'variants' ][ 0 ]
	packs: boolean
	faqs: boolean
	reviews: boolean
}

// eslint-disable-next-line max-len
export default function Nav({ product, variant, packs, faqs, reviews }: Props ) {
	const { scrollY } = useScroll()
	const { height } = useWindowSize()
	// eslint-disable-next-line max-len
	const opacity = useTransform( scrollY, [ 0, height, height + 100 ], [ 0, 0, 1 ] )
	// eslint-disable-next-line max-len
	const pointerEvents = useTransform( opacity, [ 0, .5, 1 ], [ 'none', 'none', 'auto' ] )

	const [ active, setActive ] = useState( null )
	const offsets = useRef({})
	useEffect( () => {
		if ( typeof window !== 'undefined' ) {
			const observer = new ResizeObserver( () => {
				window.requestAnimationFrame( () => {
					[ ...sections({ packs, faqs, reviews }), 'end' ].map( section => {
						const element = document.getElementById( section )
						if ( element ) offsets.current[ section ] = element.offsetTop
					})
				})
			})
			observer.observe( document.body )

			return () => observer.disconnect()
		}
	}, [] )

	useEffect( () => {
		return scrollY.onChange( () => {
			const section = Object.entries( offsets.current )
				.filter( ( [ , offset ] ) => ( offset < ( scrollY.get() + 50 ) ) )
				.reverse()
				?.[ 0 ]?.[ 0 ]

			if ( section && section !== active ) setActive( section )
		})
	}, [ active, offsets ] )

	const mobile = useMedia({ maxWidth: `${mobileMenuMaxWidth}px` })
	if ( mobile ) return <Buy variant={variant} />

	return (
		<Portal selector="#__next">
			<AnimateSharedLayout>
				<motion.div
					className="product-nav"
					initial={{ opacity: 0 }}
					style={{ opacity, pointerEvents }}
				>
					<div className="product">
						<picture>
							<img src={res( variant.image.originalSrc, '128x' )} />
						</picture>
						<div>
							<div>{product.title}</div>
							<div>
								{variant.selectedOptions
									.map( o => o.value )
									.filter( value => value !== 'Default Title' )
									.join( ' — ' )}
							</div>
						</div>
					</div>
					<Links packs={packs} faqs={faqs} reviews={reviews} active={active} />
					<BuyButton variant={variant} />
					<style jsx global>{`
						.product-nav {
							position: fixed;
							top: 0;
							width: 100%;
							padding: 15px;
							display: flex;
							align-items: center;
							justify-content: space-between;
							background: #FFFFFF;
							box-shadow: 0 4px 30px 0 rgba(0,0,0,0.11);
							text-transform: lowercase;
							font-size: 18px;
						}
						.product-nav .product {
							display: flex;
							align-items: center;
							line-height: 1.4;
						}
						.product-nav a {
							text-decoration: none;
						}
						.product-nav picture {
							width: 64px;
							height: 86px;
							margin-right: 16px;
							padding: 10px 0;
							background-color: #F7F7F7;
						}
						.product-nav picture img {
							object-fit: contain;
							image-rendering: -webkit-optimize-contrast;
						}
						.product-nav ol {
							display: flex;
							flex: 0 1 auto;
							margin: 0 30px;
							line-height: 2.83;
							overflow: hidden;
							white-space: nowrap;
							text-overflow: ellipsis;
						}
						.product-nav li {
							margin: 0 1.2em;
						}
						.product-nav li :global( a.u::after ) {
							bottom: 4px;
						}
						.product-nav :global(.button) {
							height: 60px;
							margin-right: 16px;
							padding-left: 36px;
							padding-right: 36px;
						}
					`}</style>
				</motion.div>
			</AnimateSharedLayout>
		</Portal>
	)
}

export const Links = ({ packs, faqs, reviews, active = null }) => (
	<motion.ol
		className="nostyle"
		transition={{ staggerChildren: .1, delayChildren: .85 }}
		initial="initial"
		animate="animate"
		exit="exit"
		variants={{
			animate: {},
			exit: { opacity: 0, transition: { duration: 1, ease } },
		}}
	>
		{sections({ packs, faqs, reviews }).map( section => (
			<motion.li
				key={section}
				style={{ position: 'relative' }}
				variants={{
					initial: { opacity: 0, y: 20 },
					animate: { opacity: 1, y: 0 },
				}}
				transition={{ duration: 1, ease }}
			>
				<a className="u" href={`#${section}`} onClick={fixPageNav}>
					{i.t( `product.nav.${section}` )}
				</a>
				{active === section && (
					<motion.div
						layoutId="product-nav-underline"
						style={{
							position: 'absolute',
							bottom: 4,
							left: 1,
							right: 1,
							height: 2,
							backgroundColor: 'currentColor',
						}}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					/>
				)}
			</motion.li>
		) )}
	</motion.ol>
)
